import React from 'react';
import { Badge } from '../Badge';
import { ChevronUpIcon, ChevronDownIcon, XIcon, PaperclipIcon, MailIcon, MessageSquareIcon, SendIcon, CheckCircleIcon, AlertTriangleIcon, ShieldIcon, CheckIcon, FlameIcon, AlertOctagonIcon, HelpCircleIcon, ExternalLinkIcon } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import { getTypeBadgeVariant, getStatusBadgeVariant, type ReviewDocument, type Comment, type Classification, type DocReviewState, type FamilyMember, type CaseIssue } from './types';
interface DocumentDrawerProps {
  selectedDoc: ReviewDocument;
  drawerTab: 'summary' | 'family' | 'comments';
  selectedIndex: number;
  visibleFlatDocs: ReviewDocument[];
  docState: DocReviewState;
  newComment: string;
  getDocComments: (docId: string) => Comment[];
  getDocFamily: (doc: ReviewDocument) => FamilyMember[];
  getThreadSize: (threadId: string) => number;
  setDrawerTab: (tab: 'summary' | 'family' | 'comments') => void;
  navigatePrev: () => void;
  navigateNext: () => void;
  onClose: () => void;
  onReviewAndNext: () => void;
  onUnreview: () => void;
  onClassify: (c: Classification) => void;
  onTogglePrivileged: () => void;
  pendingTbd: boolean;
  caseIssues: CaseIssue[];
  docIssues: string[];
  onToggleIssue: (issueId: string) => void;
  handlePostComment: () => void;
  setNewComment: (v: string) => void;
  commentsEndRef: React.RefObject<HTMLDivElement | null>;
  commentInputRef: React.RefObject<HTMLTextAreaElement | null>;
}
export function DocumentDrawer({
  selectedDoc,
  drawerTab,
  selectedIndex,
  visibleFlatDocs,
  docState,
  newComment,
  getDocComments,
  getDocFamily,
  getThreadSize,
  setDrawerTab,
  navigatePrev,
  navigateNext,
  onClose,
  onReviewAndNext,
  onUnreview,
  onClassify,
  onTogglePrivileged,
  pendingTbd,
  caseIssues,
  docIssues,
  onToggleIssue,
  handlePostComment,
  setNewComment,
  commentsEndRef,
  commentInputRef
}: DocumentDrawerProps) {
  return <div className="absolute top-0 right-0 bottom-0 w-[480px] bg-navy-light border-l border-navy-lighter flex flex-col shadow-2xl" data-id="element-92">
      {/* Drawer Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-navy-lighter shrink-0" data-id="element-93">
        <div className="flex items-center gap-2 min-w-0" data-id="element-94">
          <span className="font-mono text-xs text-gray-400" data-id="element-95">
            {selectedDoc.id}
          </span>
          <span className="text-gray-600" data-id="element-96">·</span>
          <Badge variant={getTypeBadgeVariant(selectedDoc.type)} data-id="element-97">
            {selectedDoc.type}
          </Badge>
        </div>
        <div className="flex items-center gap-1 shrink-0" data-id="element-98">
          <button onClick={() => window.open(`/documents/${selectedDoc.id}`, '_blank')} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-400 hover:text-amber hover:bg-navy border border-navy-lighter rounded-lg transition-colors mr-2" title="Open document in new tab" data-id="element-99">
            <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-100" />
            Open
          </button>
          <span className="text-xs text-gray-500 mr-2" data-id="element-101">
            {selectedIndex + 1 > 0 ? selectedIndex + 1 : '–'} of{' '}
            {visibleFlatDocs.length}
          </span>
          <button onClick={navigatePrev} disabled={selectedIndex <= 0} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors disabled:opacity-25 disabled:cursor-not-allowed" data-id="element-102">
            <ChevronUpIcon className="w-4 h-4" data-id="element-103" />
          </button>
          <button onClick={navigateNext} disabled={selectedIndex >= visibleFlatDocs.length - 1} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors disabled:opacity-25 disabled:cursor-not-allowed" data-id="element-104">
            <ChevronDownIcon className="w-4 h-4" data-id="element-105" />
          </button>
          <Tooltip content="Close" shortcut="Esc" position="bottom" align="end" data-id="element-106">
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white hover:bg-navy rounded transition-colors ml-1" data-id="element-107">
              <XIcon className="w-4 h-4" data-id="element-108" />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex border-b border-navy-lighter shrink-0" data-id="element-109">
        <button onClick={() => setDrawerTab('summary')} className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative ${drawerTab === 'summary' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-110">
          Summary
          {drawerTab === 'summary' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber" data-id="element-111" />}
        </button>
        <button onClick={() => setDrawerTab('family')} className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative flex items-center justify-center gap-1.5 ${drawerTab === 'family' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-112">
          <PaperclipIcon className="w-3 h-3" data-id="element-113" />
          Family
          {drawerTab === 'family' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber" data-id="element-114" />}
        </button>
        <button onClick={() => setDrawerTab('comments')} className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative flex items-center justify-center gap-1.5 ${drawerTab === 'comments' ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-115">
          <MessageSquareIcon className="w-3 h-3" data-id="element-116" />
          Comments
          {getDocComments(selectedDoc.id).length > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${drawerTab === 'comments' ? 'bg-amber/20 text-amber' : 'bg-navy-lighter text-gray-400'}`} data-id="element-117">
              {getDocComments(selectedDoc.id).length}
            </span>}
          {drawerTab === 'comments' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber" data-id="element-118" />}
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 min-h-0 flex flex-col" data-id="element-119">
        {drawerTab === 'summary' ? <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-120">
            {/* AI Summary */}
            <div data-id="element-121">
              <div className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-semibold" data-id="element-122">
                AI Summary
              </div>
              <p className="text-sm text-gray-300 leading-relaxed" data-id="element-123">
                {selectedDoc.fullSummary || selectedDoc.summary}
              </p>
            </div>

            {/* Key Metadata Grid */}
            <div className="bg-navy rounded-lg border border-navy-lighter p-3 space-y-3" data-id="element-124">
              <div data-id="element-125">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5" data-id="element-126">
                  Bates #
                </div>
                <div className="font-mono text-xs text-white" data-id="element-127">
                  {selectedDoc.id}
                </div>
              </div>

              <div className="pt-2 border-t border-navy-lighter space-y-2" data-id="element-128">
                <div data-id="element-129">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5" data-id="element-130">
                    From
                  </div>
                  <div className="text-xs text-gray-300" data-id="element-131">
                    {selectedDoc.sender}
                  </div>
                </div>
                <div data-id="element-132">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5" data-id="element-133">
                    To
                  </div>
                  <div className="text-xs text-gray-300" data-id="element-134">
                    {selectedDoc.type === 'Email' ? 'legal-team@acme.com, review-group@acme.com' : '—'}
                  </div>
                </div>
                <div data-id="element-135">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5" data-id="element-136">
                    Subject
                  </div>
                  <div className="text-xs text-white font-medium" data-id="element-137">
                    {selectedDoc.subject}
                  </div>
                </div>
              </div>

              {selectedDoc.threadId && <div className="pt-2 border-t border-navy-lighter flex items-center justify-between" data-id="element-138">
                  <div className="text-[10px] text-gray-500 uppercase tracking-wider" data-id="element-139">
                    Thread
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-300" data-id="element-140">
                    <MailIcon className="w-3 h-3 text-gray-500" data-id="element-141" />
                    {getThreadSize(selectedDoc.threadId)} emails
                    {selectedDoc.isTerminal && <Badge variant="amber" className="text-[10px] px-1 py-0 ml-1" data-id="element-142">
                        Terminal
                      </Badge>}
                  </div>
                </div>}
            </div>

            {/* Audit trail */}
            <div className="text-[10px] text-gray-500 text-center pt-1" data-id="element-143">
              Last accessed by Sarah Chen · Nov 20, 2023 at 14:32 EST
            </div>
          </div> : drawerTab === 'family' ? <div className="flex-1 overflow-y-auto p-4" data-id="element-144">
            <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider font-semibold" data-id="element-145">
              Document Family
            </div>
            <div className="space-y-1" data-id="element-146">
              {getDocFamily(selectedDoc).map((member, idx) => <div key={member.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${member.id === selectedDoc.id ? 'bg-amber/5 border border-amber/20' : 'hover:bg-navy-hover border border-transparent'} ${member.type === 'attachment' ? 'ml-4' : ''}`} data-id="element-147">
                  <div className={`w-7 h-7 rounded flex items-center justify-center shrink-0 ${member.type === 'parent' ? 'bg-blue-500/10' : 'bg-navy-lighter'}`} data-id="element-148">
                    {member.type === 'parent' ? <MailIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-149" /> : <PaperclipIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-150" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-151">
                    <div className="text-xs text-white truncate" data-id="element-152">
                      {member.name}
                    </div>
                    <div className="text-[10px] text-gray-500 flex items-center gap-2" data-id="element-153">
                      {member.type === 'parent' ? <span data-id="element-154">Parent email</span> : <>
                          {member.fileType && <span data-id="element-155">{member.fileType}</span>}
                          {member.size && <span data-id="element-156">· {member.size}</span>}
                        </>}
                      {member.id === selectedDoc.id && <span className="text-amber" data-id="element-157">← Current</span>}
                    </div>
                  </div>
                </div>)}
            </div>

            {getDocFamily(selectedDoc).length <= 1 && <div className="text-center py-8" data-id="element-158">
                <PaperclipIcon className="w-8 h-8 text-gray-600 mx-auto mb-2" data-id="element-159" />
                <p className="text-xs text-gray-500" data-id="element-160">
                  No attachments or related documents
                </p>
              </div>}
          </div> : <>
            {/* Comments Tab */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-161">
              {getDocComments(selectedDoc.id).length === 0 ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-162">
                  <MessageSquareIcon className="w-8 h-8 text-gray-600 mb-2" data-id="element-163" />
                  <p className="text-xs text-gray-400" data-id="element-164">No comments yet</p>
                  <p className="text-[10px] text-gray-500 mt-1" data-id="element-165">
                    Be the first to leave a note.
                  </p>
                </div> : getDocComments(selectedDoc.id).map(comment => <div key={comment.id} className="flex gap-2.5" data-id="element-166">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 ${comment.isCurrentUser ? 'bg-amber/20 text-amber' : 'bg-navy-lighter text-gray-300'}`} data-id="element-167">
                      {comment.initials}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-168">
                      <div className="flex items-baseline gap-2 mb-0.5" data-id="element-169">
                        <span className={`text-xs font-medium ${comment.isCurrentUser ? 'text-amber' : 'text-white'}`} data-id="element-170">
                          {comment.author}
                        </span>
                        <span className="text-[10px] text-gray-500" data-id="element-171">
                          {comment.timestamp}
                        </span>
                      </div>
                      <p className="text-xs text-gray-300 leading-relaxed" data-id="element-172">
                        {comment.text}
                      </p>
                    </div>
                  </div>)}
              <div ref={commentsEndRef} data-id="element-173" />
            </div>

            {/* Comment Input */}
            <div className="p-3 border-t border-navy-lighter shrink-0" data-id="element-174">
              <div className="flex gap-2" data-id="element-175">
                <textarea ref={commentInputRef} value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handlePostComment();
              }
            }} placeholder="Add a comment..." rows={2} className="flex-1 bg-navy border border-navy-lighter rounded-lg px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-colors resize-none" data-id="element-176" />
                <button onClick={handlePostComment} disabled={!newComment.trim()} className="self-end p-2 bg-amber hover:bg-amber-hover text-navy rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed" title="Post comment (Enter)" data-id="element-177">
                  <SendIcon className="w-3.5 h-3.5" data-id="element-178" />
                </button>
              </div>
            </div>
          </>}
      </div>

      {/* Review & Classification Footer */}
      <div className="px-4 py-4 border-t border-navy-lighter bg-[#0A1520] shrink-0 space-y-4" data-id="element-179">
        {/* Classification */}
        <div data-id="element-180">
          <div className="flex gap-2" data-id="element-181">
            <button onClick={() => onClassify('hot')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${docState.classification === 'hot' ? 'bg-red-500/15 border-red-500/40 text-red-400' : 'bg-navy border-navy-lighter text-gray-400 hover:bg-navy-hover hover:text-gray-200'}`} data-id="element-182">
              <FlameIcon className="w-3.5 h-3.5" data-id="element-183" />
              Hot
            </button>
            <button onClick={() => onClassify('critical')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${docState.classification === 'critical' ? 'bg-orange-500/15 border-orange-500/40 text-orange-400' : 'bg-navy border-navy-lighter text-gray-400 hover:bg-navy-hover hover:text-gray-200'}`} data-id="element-184">
              <AlertOctagonIcon className="w-3.5 h-3.5" data-id="element-185" />
              Critical
            </button>
            <button onClick={() => onClassify('tbd')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${pendingTbd ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300 animate-pulse' : docState.classification === 'tbd' ? 'bg-yellow-500/15 border-yellow-500/40 text-yellow-400' : 'bg-navy border-navy-lighter text-gray-400 hover:bg-navy-hover hover:text-gray-200'}`} data-id="element-186">
              <HelpCircleIcon className="w-3.5 h-3.5" data-id="element-187" />
              {pendingTbd ? 'TBD — awaiting comment' : 'TBD'}
            </button>
          </div>
          {pendingTbd && <div className="mt-2 text-[10px] text-yellow-400 flex items-center gap-1" data-id="element-188">
              <AlertTriangleIcon className="w-3 h-3" data-id="element-189" />
              Post a comment to confirm TBD classification
            </div>}
        </div>

        {/* Issues */}
        <div className="pt-3 border-t border-navy-lighter" data-id="element-190">
          <div className="flex flex-wrap gap-1.5" data-id="element-191">
            {caseIssues.map(issue => {
            const isTagged = docIssues.includes(issue.id);
            return <button key={issue.id} onClick={() => onToggleIssue(issue.id)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-medium transition-all duration-200 border ${isTagged ? 'border-transparent' : 'border-navy-lighter text-gray-500 hover:text-gray-300 hover:border-gray-600 bg-navy'}`} style={isTagged ? {
              backgroundColor: issue.color + '20',
              color: issue.color,
              borderColor: issue.color + '40'
            } : {}} data-id="element-192">
                  <div className="w-2 h-2 rounded-full shrink-0 transition-colors" style={{
                backgroundColor: isTagged ? issue.color : '#4b5563'
              }} data-id="element-193" />
                  {issue.label}
                </button>;
          })}
          </div>
        </div>

        {/* Privileged Toggle */}
        <div className={`pt-3 border-t flex items-center justify-between ${docState.privileged ? 'border-red-500/30' : 'border-navy-lighter'}`} data-id="element-194">
          <div className="flex items-center gap-2" data-id="element-195">
            <ShieldIcon className={`w-4 h-4 ${docState.privileged ? 'text-red-400' : 'text-gray-500'}`} data-id="element-196" />
            <div data-id="element-197">
              <div className={`text-xs font-medium ${docState.privileged ? 'text-red-400' : 'text-gray-400'}`} data-id="element-198">
                Privileged
              </div>
              {docState.privileged && docState.privilegedBy && <div className="text-[9px] text-red-400/60" data-id="element-199">
                  Set by {docState.privilegedBy}
                </div>}
            </div>
          </div>
          <button onClick={onTogglePrivileged} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${docState.privileged ? 'bg-red-500' : 'bg-navy-lighter'}`} data-id="element-200">
            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${docState.privileged ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-201" />
          </button>
        </div>

        {/* Review & Next — at the very bottom */}
        <div className="pt-3 border-t border-navy-lighter flex items-center justify-between" data-id="element-202">
          <div className="flex items-center gap-2" data-id="element-203">
            {docState.reviewed ? <div className="flex items-center gap-1.5 text-green-400 text-xs font-medium" data-id="element-204">
                <CheckCircleIcon className="w-4 h-4" data-id="element-205" />
                Reviewed by {docState.reviewedBy}
              </div> : <div className="text-gray-500 text-xs" data-id="element-206">Not reviewed</div>}
          </div>
          <div className="flex items-center gap-2" data-id="element-207">
            {docState.reviewed && <button onClick={onUnreview} className="flex items-center gap-2 px-3 py-2 bg-navy border border-navy-lighter hover:border-red-500/40 hover:bg-red-500/10 text-gray-400 hover:text-red-400 font-medium rounded-lg transition-colors text-xs" data-id="element-208">
                Unreview
              </button>}
            <Tooltip content={docState.reviewed ? 'Already reviewed' : 'Review & Next'} shortcut={docState.reviewed ? undefined : 'R'} data-id="element-209">
              <button onClick={onReviewAndNext} disabled={docState.reviewed} className={`flex items-center gap-2 px-4 py-2 font-semibold rounded-lg transition-colors text-sm ${docState.reviewed ? 'bg-green-500/15 text-green-400 border border-green-500/30 cursor-default' : 'bg-amber hover:bg-amber-hover text-navy shadow-lg shadow-amber/20'}`} data-id="element-210">
                <CheckIcon className="w-4 h-4" data-id="element-211" />
                {docState.reviewed ? 'Reviewed' : 'Reviewed & Next'}
                {!docState.reviewed && <span className="text-navy/50 text-xs font-medium ml-1" data-id="element-212">
                    (R)
                  </span>}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>;
}